import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { Database } from "~/integrations/supabase/types";

type ContactAttempt = Database["public"]["Tables"]["contact_attempts"]["Row"];

interface CartItem {
  name: string;
  quantity: number;
}

/**
 * Normalize the `cart_items` JSON, which may arrive as an array, an object with
 * an `items` array, or a JSON string. Mirrors the parsing in
 * CotizacionDetailPage.tsx so the export matches what the detail view shows.
 */
const parseCartItems = (cartItems: unknown): CartItem[] => {
  if (!cartItems) return [];

  try {
    if (Array.isArray(cartItems)) return cartItems as CartItem[];

    if (typeof cartItems === "object") {
      const items = (cartItems as { items?: unknown }).items;
      if (Array.isArray(items)) return items as CartItem[];
    }

    if (typeof cartItems === "string") {
      const parsed = JSON.parse(cartItems);
      if (Array.isArray(parsed)) return parsed as CartItem[];
      if (parsed && Array.isArray(parsed.items)) return parsed.items as CartItem[];
    }
  } catch {
    return [];
  }

  return [];
};

/** Summarize requested products as "ACEITE X x1; ACEITE Y x2". */
const summarizeProducts = (items: CartItem[]) =>
  items
    .map((item) => `${item.name ?? ""} x${item.quantity ?? 1}`.trim())
    .join("; ");

/**
 * Export the (already filtered) cotizaciones list to an .xlsx workbook,
 * matching what is shown on screen. Returns the generated filename.
 */
export function exportCotizacionesToExcel(cotizaciones: ContactAttempt[]) {
  const rows = cotizaciones.map((c) => {
    const items = parseCartItems(c.cart_items);
    return {
      Nombre: c.full_name ?? "",
      Empresa: c.company ?? "",
      Email: c.email ?? "",
      Teléfono: c.phone ?? "",
      Ubicación: c.location ?? "",
      Tipo: c.is_quotation === true ? "Cotización" : "Consulta",
      Fuente: c.source === "nexor" ? "Nexor" : "Web",
      Estado: c.is_read ? "Leído" : "Nuevo",
      Fecha: c.created_at ? dayjs(c.created_at).format("DD-MM-YYYY HH:mm") : "",
      "N° Productos": items.length,
      Productos: summarizeProducts(items),
      Mensaje: c.message ?? "",
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 28 }, { wch: 28 }, { wch: 30 }, { wch: 16 }, { wch: 22 },
    { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 12 },
    { wch: 50 }, { wch: 60 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Cotizaciones");

  const filename = `cotizaciones_${dayjs().format("YYYY-MM-DD")}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}
